/**
 * OAuth 2.0 PKCE (Proof Key for Code Exchange) Utilities
 * Provides helper functions for implementing OAuth 2.0 Authorization Code Flow with PKCE
 */

/**
 * Base64 URL encode (without padding)
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...Array.from(buffer)))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Generate a random string for code verifier or state parameter
 */
function generateRandomString(length: number): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return base64UrlEncode(array)
}

/**
 * Generate SHA-256 hash
 */
async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const data = encoder.encode(plain)
  return crypto.subtle.digest('SHA-256', data)
}

/**
 * Generate PKCE code verifier (random string)
 */
export function generateCodeVerifier(): string {
  return generateRandomString(32)
}

/**
 * Generate PKCE code challenge from verifier using S256 method
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const hashed = await sha256(verifier)
  return base64UrlEncode(new Uint8Array(hashed))
}

/**
 * Generate state parameter for CSRF protection
 */
export function generateState(): string {
  return generateRandomString(16)
}

/**
 * Generate nonce for OIDC token replay protection
 */
export function generateNonce(): string {
  return generateRandomString(16)
}

/**
 * Build OAuth 2.0 authorization URL with PKCE and OIDC parameters
 */
export function buildAuthorizationUrl(params: {
  authorizationEndpoint: string
  clientId: string
  redirectUri: string
  scope: string
  state: string
  codeChallenge: string
  nonce?: string
  prompt?: 'login' | 'none' | 'consent' | 'select_account'
}): string {
  const { authorizationEndpoint, clientId, redirectUri, scope, state, codeChallenge, nonce, prompt } =
    params

  const urlParams = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  // Add nonce for OIDC token replay protection
  if (nonce) {
    urlParams.set('nonce', nonce)
  }

  if (prompt) {
    urlParams.set('prompt', prompt)
  }

  return `${authorizationEndpoint}?${urlParams.toString()}`
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForToken(params: {
  tokenEndpoint: string
  clientId: string
  redirectUri: string
  code: string
  codeVerifier: string
}): Promise<{
  id_token: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  token_type?: string
}> {
  const { tokenEndpoint, clientId, redirectUri, code, codeVerifier } = params

  const body = new URLSearchParams({
    client_id: clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  })

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'unknown_error' }))
    throw new Error(`Token exchange failed: ${errorData.error || response.statusText}`)
  }

  return response.json()
}
